import { LocationChangePayload } from 'connected-react-router';
import { getBuildType } from 'src/helpers';
import { BuildEnv } from 'src/typings/env';
import {
  call, put, select, takeEvery,
} from 'redux-saga/effects';
import { Actions, ActionTypes } from 'src/store/marketplace/actions';
import {
  IMarketplace,
  IMarketplaceRequestFilter,
  IViewTile,
} from 'src/components/profileComponents/Marketplace/types';
import {
  getCurrentMarketplace,
  getMarketplaceViewTile,
  getMarketplaceWhereConditions,
} from 'src/store/marketplace/selectors';
import {
  IMarketplaceFilterResponse,
  IMarketplaceSubFilterResponse,
} from 'src/typings/marketplace';
import { IMarketplaceTheme } from 'src/components/Marketplace/theme-types';

export function* setMarketplaceIdWatchSaga(action: {
  type: string,
  payload: LocationChangePayload,
}) {
  const buildType = getBuildType();
  const isMarketplace = buildType === BuildEnv.marketplace;
  if (action.payload.isFirstRendering && isMarketplace) {
    let marketplaceIdString: string;
    if (window.marketplaceId === 'MARKETPLACE_ID') {
      yield put(Actions.setMarketplaceIsLoading(true));
      const string = new URL(document.URL).pathname.split('/');
      marketplaceIdString = string[string.length - 1];
    } else {
      marketplaceIdString = window.marketplaceId;
    }

    const themeResponse: IMarketplaceTheme = yield call(async () => {
      const fetchResponse = await fetch(`${process.env.REACT_APP_REST_API}/api/cntrlr/marketplaces__styles/${marketplaceIdString}`);
      const data = await fetchResponse.json();
      return data;
    });
    yield put(Actions.setMarketplaceThemeConfig({
      fonts: {
        fontFamily: ['Roboto'],
        fontWeight: ['100', '200', '300', '400', '500', '600', '700', '800', '900'],
        xs: { fontFamily: 'Inter' },
        sm: { fontFamily: 'Inter' },
        md: { fontFamily: 'Inter' },
        lg: { fontFamily: themeResponse.font.headerText.fontName, fontWeight: themeResponse.font.headerText.fontWeight },
      },
      text: themeResponse.text,
      userInterfaceElements: themeResponse.userInterfaceElements,
      background: themeResponse.background,
      button: themeResponse.button,
    }));

    // @ts-ignore
    const response: IMarketplace = yield call(async () => {
      const fetchResponse = await fetch(`${process.env.REACT_APP_REST_API}/api/cntrlr/marketplaces/${marketplaceIdString}`);
      const data = await fetchResponse.json();
      return data;
    });
    const sortOrderElements = response.frontConfiguration.module.placementTile.propertiesSortOrder.elements;
    const defaultValue = sortOrderElements.find((item) => item.isDefault);
    const firstValue = {
      id: sortOrderElements[0]['@id'],
      propertySystemName: sortOrderElements[0].property?.systemName || null,
      orderDirection: sortOrderElements[0].sortOrderDirection,
    };
    yield put(Actions.setMarketplaceSortingOrderState(defaultValue && defaultValue.property?.systemName ? [{
      id: defaultValue['@id'],
      propertySystemName: defaultValue.property?.systemName || null,
      orderDirection: defaultValue.sortOrderDirection,
    }] : [firstValue]));
    yield put(Actions.setCurrentMarketplace(response));
    yield put(Actions.setMarketplaceLanguage(response?.language));
  }
}

export function* setMarketplaceInitialFilters(action: {
  type: string,
  payload: LocationChangePayload,
}) {
  const marketplace: IMarketplace = yield select(getCurrentMarketplace);
  if (marketplace) {
    yield put(Actions.setMarketplaceIsLoading(true));
    const viewTile: IViewTile = yield select(getMarketplaceViewTile);
    const filterResponse: IMarketplaceFilterResponse = yield call(async () => {
      const fetchResponse = await fetch(`${process.env.REACT_APP_REST_API}/api/cntrlr/marketplaces/filter`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/ld+json',
          },
          body: JSON.stringify({
            language: marketplace.language,
            marketplaceId: marketplace.id,
            viewTile: {
              ...viewTile,
              sortOrders: viewTile?.sortOrders?.[0]?.propertySystemName ? [{
                propertySystemName: viewTile?.sortOrders?.[0].propertySystemName,
                orderDirection: viewTile?.sortOrders?.[0].orderDirection,
              }] : null,
            },
          }),
        });
      const data = await fetchResponse.json();
      return data;
    });
    yield put(Actions.setMarketplaceFiltersInitial(filterResponse.filter));
    yield put(Actions.setMarketplaceComplexesTile(filterResponse.complexesTile));
    yield put(Actions.setMarketplacePlacementsTile(filterResponse.placementsTile));
    yield put(Actions.setMarketplaceComplexesMapList(filterResponse.complexesMapList));
    yield put(Actions.setMarketplaceGeneralUnitInfo(filterResponse.generalUnitInfo));
    yield put(Actions.setMarketplacePaginationData(filterResponse.pagination));
    yield put(Actions.setMarketplaceIsLoading(false));
  }
}

export function* setMarketplaceFilters(action: {
  type: string,
  payload: LocationChangePayload,
}) {
  const marketplace: IMarketplace = yield select(getCurrentMarketplace);
  const whereConditions: IMarketplaceRequestFilter[] = yield select(getMarketplaceWhereConditions);
  const viewTile: IViewTile = yield select(getMarketplaceViewTile);
  if (marketplace && viewTile) {
    yield put(Actions.setMarketplaceIsLoading(true));
    const filterResponse: IMarketplaceFilterResponse = yield call(async () => {
      const fetchResponse = await fetch(`${process.env.REACT_APP_REST_API}/api/cntrlr/marketplaces/filter`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/ld+json',
          },
          body: JSON.stringify({
            language: marketplace.language,
            marketplaceId: marketplace.id,
            whereConditions,
            viewTile: {
              ...viewTile,
              sortOrders: viewTile?.sortOrders?.[0]?.propertySystemName ? [{
                propertySystemName: viewTile?.sortOrders?.[0].propertySystemName,
                orderDirection: viewTile?.sortOrders?.[0].orderDirection,
              }] : null,
            },
          }),
        });
      const data = await fetchResponse.json();
      return data;
    });
    const searchMethod = (subValues: IMarketplaceSubFilterResponse[], searchPropertyName: string): boolean => {
      return !!subValues.find((subValue) => {
        if (subValue.propertyName === searchPropertyName) { return true; }
        return !!subValue.values.find((value) => {
          return searchMethod(value.subValues, searchPropertyName);
        });
      });
    };
    const newWhereConditions = whereConditions.filter((condition) => {
      const foundFilter = filterResponse.filter.find((filter) => {
        return !!filter.values.find((filterValue) => {
          return searchMethod(filterValue.subValues, condition.propertyName);
        });
      });
      if (foundFilter) {
        return foundFilter.values.find((filter) => {
          if ('equals' in condition) {
            return filter.value === condition?.equals;
          }
          return true;
        });
      }
      return false;
    });
    if (newWhereConditions.length && whereConditions.length && newWhereConditions.length !== whereConditions.length) {
      yield put(Actions.setMarketplaceWhereConditionsWithoutRequest(newWhereConditions));
    }
    yield put(Actions.setMarketplaceFilters(filterResponse.filter));
    yield put(Actions.setMarketplaceComplexesTile(filterResponse.complexesTile));
    yield put(Actions.setMarketplacePlacementsTile(filterResponse.placementsTile));
    yield put(Actions.setMarketplaceComplexesMapList(filterResponse.complexesMapList));
    yield put(Actions.setMarketplaceGeneralUnitInfo(filterResponse.generalUnitInfo));
    yield put(Actions.setMarketplacePaginationData(filterResponse.pagination));
    yield put(Actions.setMarketplaceIsLoading(false));
  }
}

export function* getMarketplaceIdWatchSaga() {
  yield takeEvery('@@router/LOCATION_CHANGE', setMarketplaceIdWatchSaga);
  yield takeEvery([ActionTypes.SET_MARKETPLACE], setMarketplaceInitialFilters);
  yield takeEvery([
    ActionTypes.SET_MARKETPLACE_FILTERS_WHERE_CONDITIONS,
    ActionTypes.SET_MARKETPLACE_RESET_FILTERS_WHERE_CONDITIONS,
    ActionTypes.SET_MARKETPLACE_RESET_ALL_FILTERS_WHERE_CONDITIONS,
    ActionTypes.SET_MARKETPLACE_VIEW_TILE,
    ActionTypes.SET_MARKETPLACE_SORTING_ORDER,
  ], setMarketplaceFilters);
}
